import type { Permission } from '@munifocus/access';
import { CASEWORK } from '../components/shell/navigation';
import type { OperationsStatus } from '@munifocus/contracts-gov';

/**
 * Where `/` takes an account that cannot do casework in its active scope.
 *
 * The dashboard is a casework screen: every request it makes needs `file:read`
 * or `file:create`. Since enforcement seats, a seated company administrator or
 * supervisor holding no officer appointment at any town reads no files — and
 * an unseated administrator reads none anywhere — so landing them on the
 * dashboard showed a page whose every panel was refused. Each of them can
 * still work somewhere; this sends them there.
 */
export type HomeDestination =
  | { kind: 'dashboard' }
  | { kind: 'redirect'; to: string }
  /** Still finding out — render nothing that fires requests. */
  | { kind: 'wait' }
  | { kind: 'empty' };

/** Holding any of these is enough to have work on the administration screen. */
export const ADMINISTRATION_PERMISSIONS: Permission[] = [
  'employee:read',
  'member:read',
  'officer:read',
  'contract:read',
];

export function homeDestination(input: {
  /** Null when there is no scope provider (a bare render) — keep the dashboard. */
  can: ((permission: Permission) => boolean) | null;
  scopeLoading: boolean;
  operations: OperationsStatus | null | undefined;
  operationsLoading: boolean;
}): HomeDestination {
  if (!input.can) return { kind: 'dashboard' };
  if (input.scopeLoading) return { kind: 'wait' };
  const can = input.can;
  if (CASEWORK.some((p) => can(p))) return { kind: 'dashboard' };
  if (can('file:public_read')) return { kind: 'redirect', to: '/public-files' };
  if (input.operations?.municipalities.some((m) => m.canDispatch)) {
    return { kind: 'redirect', to: '/operations/dispatch' };
  }
  if (input.operationsLoading) return { kind: 'wait' };
  if (ADMINISTRATION_PERMISSIONS.some((p) => can(p))) {
    return { kind: 'redirect', to: '/administration' };
  }
  return { kind: 'empty' };
}
